import React, { useEffect, useState, useRef } from "react";
import { Redirect, useLocation } from "wouter";
import {
  getCurrentShop,
  getCurrentUser,
  checkOwnerSuspension,
  setOwnerSuspension,
} from "@/lib/auth";
import { getShopInventoryWithCart, getShopCashRegisters } from "@/lib/api";
import { indexedDBService } from "@/lib/db/indexed-db";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, AlertTriangle } from "lucide-react";

interface ProtectedShopRouteProps {
  component: React.ComponentType;
  requiredRoles?: Array<"supervisor" | "store_manager" | "cashier">;
}

export function ProtectedShopRoute({
  component: Component,
  requiredRoles = [],
}: ProtectedShopRouteProps) {
  const [location, setLocation] = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isSuspended, setIsSuspended] = useState(false);
  const shop = getCurrentShop();
  const user = getCurrentUser();

  useEffect(() => {
    const verifyAccess = async () => {
      // Verificar si hay sesión
      if (!shop || !user) {
        setIsChecking(false);
        return;
      }

      try {
        // Verificar si el owner está suspendido
        const suspended = await checkOwnerSuspension();
        setIsSuspended(suspended);
        setOwnerSuspension(suspended);

        // Si está suspendido, redirigir a la página de suspensión
        if (suspended) {
          setLocation("/shop/suspended");
        }
      } catch (error) {
        console.error("Error verificando acceso:", error);
      } finally {
        setIsChecking(false);
      }
    };

    verifyAccess();
  }, [shop?.id, user?.id]);

  // Precargar inventario en IndexedDB al entrar al modo tienda (para uso offline)
  const precacheDoneRef = useRef(false);
  useEffect(() => {
    if (!shop || !navigator.onLine || precacheDoneRef.current) return;
    precacheDoneRef.current = true;

    const precacheInventory = async () => {
      try {
        console.log("📦 [Precache] Cargando inventario completo para offline...");
        const result = await getShopInventoryWithCart(shop.id, [], {
          page: 1,
          limit: 1000,
          search: "",
        });
        if (result.filtered?.data?.length > 0) {
          await indexedDBService.saveFullInventoryForOffline(
            shop.id,
            result.filtered.data,
          );
          console.log(
            `✅ [Precache] Inventario guardado para offline: ${result.filtered.data.length} productos`,
          );
        }

        // Precargar cajas registradoras para uso offline
        try {
          const registers = await getShopCashRegisters(shop.id);
          if (registers.length > 0) {
            await indexedDBService.saveCashRegisters(shop.id, registers);
            console.log(`✅ [Precache] Cajas registradoras guardadas para offline: ${registers.length}`);
          }
        } catch (regError) {
          console.warn("⚠️ [Precache] No se pudieron precargar cajas:", regError);
        }
      } catch (error) {
        console.warn("⚠️ [Precache] No se pudo precargar inventario:", error);
        precacheDoneRef.current = false; // Permitir reintento
      }
    };

    precacheInventory();
  }, [shop?.id]);

  // Mostrar nada mientras verificamos
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  // Si no hay sesión, redirigir al login
  if (!shop || !user) {
    return <Redirect to="/shop/login" />;
  }

  // Si está suspendido, no renderizar nada (la redirección ya ocurrió en el useEffect)
  if (isSuspended) {
    return null;
  }

  // Verificar roles
  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role as any)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground mb-4">
              No tienes permiso para acceder a esta página.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Esta sección requiere permisos de {requiredRoles.join(" o ")}.
            </p>
            <Button onClick={() => window.history.back()}>Volver</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <Component />;
}
